import React, { useState, useEffect } from 'react';
import { optimizeLinkedIn } from '../services/openaiService';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import Header from './Header';
import Footer from './Footer';
import './LinkedInOptimizerPage.css';
import ProductSidebar from './ProductSidebar';


const SECTIONS = [
  { id: 'headline', label: 'Headline', icon: '✏️' },
  { id: 'summary', label: 'About / Summary', icon: '📝' },
  { id: 'experience', label: 'Experience', icon: '💼' },
  { id: 'skills', label: 'Skills', icon: '⚡' },
  { id: 'education', label: 'Education', icon: '🎓' },
];

const LinkedInOptimizerPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Input state
  const [linkedInUrl, setLinkedInUrl] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [targetIndustry, setTargetIndustry] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [activeSection, setActiveSection] = useState('headline');

  // Profile data (auto-loaded from saved profile)
  const [profileData, setProfileData] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Results state
  const [results, setResults] = useState({});
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizedSection, setOptimizedSection] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState({});
  const [overallScore, setOverallScore] = useState(null);

  // Load user profile on mount
  useEffect(() => {
    if (user) {
      fetchUserProfile();
    }
  }, [user]);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.profile) {
        setProfileData(data.profile);
        setProfileLoaded(true);
        if (data.profile.linkedIn) {
          setLinkedInUrl(data.profile.linkedIn);
        }
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    }
  };

  const handleOptimize = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (!targetRole.trim()) {
      setError('Please enter your target role.');
      return;
    }

    setIsOptimizing(true);
    setError('');
    setResults({});
    setOverallScore(null);

    try {
      const data = await optimizeLinkedIn(linkedInUrl, targetRole, targetIndustry, jobDescription, profileData);
      setResults(data.optimizations || {});
      setOverallScore(data.overallScore || null);
      setOptimizedSection(activeSection);
    } catch (err) {
      setError(err.message || 'Failed to optimize. Please try again.');
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleCopy = (sectionId, text) => {
    navigator.clipboard.writeText(text);
    setCopied((prev) => ({ ...prev, [sectionId]: true }));
    setTimeout(() => setCopied((prev) => ({ ...prev, [sectionId]: false })), 2000);
  };

  const hasResults = Object.keys(results).length > 0;

  return (
    <div className="neural-glass-homepage">
      <div className="neural-background"></div>
      <div className="geometric-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>

      <div className="li-page-container">
        <Header setShowAuthModal={setShowAuthModal} />
        <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

        <main className="li-page-main">
          {/* Hero */}
          <div className="li-hero">
            <div className="li-hero-badge">🚀 AI-Powered</div>
            <h1 className="li-hero-title">
              LinkedIn <span className="li-gradient-text">Profile Optimizer</span>
            </h1>
            <p className="li-hero-subtitle">
              Transform your LinkedIn profile to attract recruiters, beat the algorithm, and land your dream role.
            </p>
          </div>

          <div className="li-main-grid">
            {/* LEFT: Input Panel */}
            <div className="li-input-panel">
              {/* LinkedIn URL */}
              <div className="li-card">
                <div className="li-card-header">
                  <span className="li-card-icon">🔗</span>
                  <h3>LinkedIn Profile URL</h3>
                </div>
                <input
                  type="url"
                  className="li-input"
                  value={linkedInUrl}
                  onChange={(e) => setLinkedInUrl(e.target.value)}
                  placeholder="https://linkedin.com/in/yourprofile"
                  disabled={isOptimizing}
                />
              </div>

              {/* Target Role & Industry */}
              <div className="li-card">
                <div className="li-card-header">
                  <span className="li-card-icon">🎯</span>
                  <h3>Target Position</h3>
                </div>
                <div className="li-field-group">
                  <label className="li-label">Target Role *</label>
                  <input
                    type="text"
                    className="li-input"
                    value={targetRole}
                    onChange={(e) => { setTargetRole(e.target.value); setError(''); }}
                    placeholder="e.g. Senior Software Engineer"
                    disabled={isOptimizing}
                  />
                </div>
                <div className="li-field-group" style={{ marginTop: '14px' }}>
                  <label className="li-label">Target Industry</label>
                  <input
                    type="text"
                    className="li-input"
                    value={targetIndustry}
                    onChange={(e) => setTargetIndustry(e.target.value)}
                    placeholder="e.g. FinTech, Healthcare, SaaS"
                    disabled={isOptimizing}
                  />
                </div>
              </div>

              {error && <div className="li-error">{error}</div>}

              {/* Optimize Button */}
              <button
                className="li-optimize-btn"
                onClick={handleOptimize}
                disabled={isOptimizing || !targetRole.trim()}
              >
                {isOptimizing ? (
                  <span className="li-loading">
                    <span className="typing-indicator">
                      <span></span><span></span><span></span>
                    </span>
                    Optimizing Profile...
                  </span>
                ) : (
                  '✨ Optimize My LinkedIn'
                )}
              </button>
            </div>

            {/* RIGHT: Results Panel */}
            <div className="li-results-panel">
              {/* Score Card */}
              {overallScore !== null && (
                <div className="li-score-card">
                  <div className="li-score-circle">
                    <svg viewBox="0 0 120 120" className="li-score-svg">
                      <circle cx="60" cy="60" r="50" className="li-score-bg" />
                      <circle
                        cx="60" cy="60" r="50"
                        className="li-score-fill"
                        strokeDasharray={`${(overallScore / 100) * 314} 314`}
                      />
                    </svg>
                    <div className="li-score-value">{overallScore}</div>
                  </div>
                  <div className="li-score-info">
                    <h3>Profile Score</h3>
                    <p>{overallScore >= 80 ? '🔥 Excellent profile strength!' : overallScore >= 60 ? '👍 Good — apply suggestions below' : '⚡ Needs improvement — use the suggestions'}</p>
                  </div>
                </div>
              )}

              {/* Section Tabs */}
              {hasResults && (
                <div className="li-tabs">
                  {SECTIONS.map((s) => (
                    <button
                      key={s.id}
                      className={`li-tab ${activeSection === s.id ? 'li-tab--active' : ''} ${results[s.id] ? 'li-tab--has-result' : ''}`}
                      onClick={() => setActiveSection(s.id)}
                    >
                      <span>{s.icon}</span>
                      <span>{s.label}</span>
                      {results[s.id] && <span className="li-tab-dot" />}
                    </button>
                  ))}
                </div>
              )}

              {/* Section Results */}
              {hasResults && results[activeSection] ? (
                <div className="li-result-card">
                  <div className="li-result-header">
                    <h3>{SECTIONS.find(s => s.id === activeSection)?.icon} {SECTIONS.find(s => s.id === activeSection)?.label} Optimization</h3>
                    <button
                      className="li-copy-btn"
                      onClick={() => handleCopy(activeSection, results[activeSection]?.optimized || '')}
                    >
                      {copied[activeSection] ? '✅ Copied!' : '📋 Copy'}
                    </button>
                  </div>

                  {/* Issues */}
                  {results[activeSection]?.issues?.length > 0 && (
                    <div className="li-issues">
                      <h4>⚠️ Current Issues</h4>
                      <ul>
                        {results[activeSection].issues.map((issue, i) => (
                          <li key={i}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Optimized Content */}
                  <div className="li-optimized-content">
                    <h4>✨ Optimized Version</h4>
                    <div className="li-optimized-text">
                      {results[activeSection]?.optimized}
                    </div>
                  </div>

                  {/* Tips */}
                  {results[activeSection]?.tips?.length > 0 && (
                    <div className="li-tips">
                      <h4>💡 Pro Tips</h4>
                      <ul>
                        {results[activeSection].tips.map((tip, i) => (
                          <li key={i}>{tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Keywords */}
                  {results[activeSection]?.keywords?.length > 0 && (
                    <div className="li-keywords">
                      <h4>🔑 Keywords to Include</h4>
                      <div className="li-keyword-chips">
                        {results[activeSection].keywords.map((kw, i) => (
                          <span key={i} className="li-keyword-chip">{kw}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : !hasResults ? (
                <div className="li-empty-state">
                  <div className="li-empty-icon">💼</div>
                  <h3>Ready to Optimize</h3>
                  <p>Fill in your target role and click <strong>Optimize My LinkedIn</strong> to get AI-powered suggestions for every section of your profile.</p>
                  <div className="li-empty-features">
                    {SECTIONS.map((s) => (
                      <div key={s.id} className="li-empty-feature">
                        <span>{s.icon}</span>
                        <span>{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="li-empty-state">
                  <p>No optimization available for this section yet.</p>
                </div>
              )}
            </div>
          </div>

          {/* Info Section */}
          <section className="li-info-section">
            <h2 className="li-section-title">Why Optimize Your LinkedIn?</h2>
            <div className="li-info-grid">
              {[
                { icon: '🎯', title: 'Beat the Algorithm', desc: 'LinkedIn ranks profiles by keyword relevance. Our AI ensures your profile is packed with the right terms recruiters search for.' },
                { icon: '👀', title: 'Get 5x More Views', desc: 'Optimized profiles receive dramatically more recruiter views. A strong headline and summary are your first impression.' },
                { icon: '🤝', title: 'More Connection Requests', desc: 'A polished profile attracts industry professionals and opens doors to opportunities you didn\'t even know existed.' },
                { icon: '💼', title: 'Land Better Interviews', desc: 'Recruiters spend just 7 seconds on a profile. Make every word count with AI-crafted, compelling content.' },
              ].map((item, i) => (
                <div key={i} className="li-info-card">
                  <div className="li-info-icon">{item.icon}</div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default LinkedInOptimizerPage;